import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, TrendingUp, FileText, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube
} from 'lucide-react'
import { useProgressStore, PASS_SCORE } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'

// ────────────────────────────────────────────────────────────────────────────
// 상수
// ────────────────────────────────────────────────────────────────────────────
const UNIT_ID = 'F2-1'

// ────────────────────────────────────────────────────────────────────────────
// 시험 문항 타입
// ────────────────────────────────────────────────────────────────────────────
interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number          // 0-based index
  explanation: string[]   // 단계별 풀이
  concept: string
  misconception?: string
  misconceptionCode?: string   // 'M01'~'M08' — progressStore 오개념 기록에 사용
  learnStepId?: string         // 연결 개념 학습 단계 ID (오답→학습 연결)
}

// ────────────────────────────────────────────────────────────────────────────
// 20문항 시험 데이터
// ────────────────────────────────────────────────────────────────────────────
const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'T01', number: 1, category: '기울기·y절편', difficulty: 'easy',
    question: 'y = −4x + 7 에서 기울기와 y절편을 바르게 나타낸 것은?',
    choices: ['기울기: 7, y절편: −4', '기울기: −4, y절편: 7', '기울기: 4, y절편: 7', '기울기: −4, y절편: −7'],
    answer: 1,
    explanation: [
      'y = ax + b 형태에서 a = 기울기, b = y절편입니다.',
      'y = −4x + 7 에서 a = −4, b = 7',
      '따라서 기울기 = −4, y절편 = 7'
    ],
    concept: '기울기·y절편', misconceptionCode: 'M03', learnStepId: 'LS05',
    misconception: 'y = −4x + 7에서 기울기와 y절편을 바꿔 읽는 실수를 조심하세요!'
  },
  {
    id: 'T02', number: 2, category: '좌표·사분면', difficulty: 'easy',
    question: '점 P(−2, −5)가 속한 사분면은?',
    choices: ['제1사분면', '제2사분면', '제3사분면', '제4사분면'],
    answer: 2,
    explanation: [
      '사분면 판단: x부호 먼저, y부호 나중에',
      'x = −2 < 0 (음수), y = −5 < 0 (음수)',
      '(−, −) → 제3사분면'
    ],
    concept: '좌표평면·사분면', misconceptionCode: 'M02', learnStepId: 'LS02',
  },
  {
    id: 'T03', number: 3, category: 'x절편', difficulty: 'easy',
    question: 'y = 3x − 9 의 x절편은?',
    choices: ['1', '3', '9', '−3'],
    answer: 1,
    explanation: [
      'x절편: y = 0 을 대입하여 x를 구합니다',
      '0 = 3x − 9',
      '3x = 9 → x = 3',
      'x절편 = 3'
    ],
    concept: 'x절편', learnStepId: 'LS06',
  },
  {
    id: 'T04', number: 4, category: '함수식 세우기', difficulty: 'easy',
    question: '기울기가 5이고 y절편이 −2인 일차함수의 식은?',
    choices: ['y = −2x + 5', 'y = 5x − 2', 'y = 5x + 2', 'y = 2x − 5'],
    answer: 1,
    explanation: [
      'y = ax + b 에서 a = 기울기, b = y절편',
      '기울기 a = 5, y절편 b = −2',
      'y = 5x + (−2) = 5x − 2'
    ],
    concept: '일차함수 식', learnStepId: 'LS07',
  },
  {
    id: 'T05', number: 5, category: '정비례', difficulty: 'easy',
    question: '다음 중 원점(0, 0)을 지나는 함수는?',
    choices: ['y = 2x + 1', 'y = −3x + 3', 'y = 4x', 'y = x − 1'],
    answer: 2,
    explanation: [
      '원점을 지나는 함수: x=0일 때 y=0이어야 합니다.',
      'y = 4x에 x=0 대입: y = 4(0) = 0 ✓',
      '정비례 함수(y=ax)는 항상 원점을 통과!'
    ],
    concept: '정비례·원점', misconceptionCode: 'M04', learnStepId: 'LS08',
    misconception: '정비례 함수(y=ax)는 y절편이 0이므로 원점을 지납니다!'
  },
  {
    id: 'T06', number: 6, category: '변화율', difficulty: 'medium',
    question: '두 점 (1, 2)와 (4, 11)을 지나는 일차함수의 기울기는?',
    choices: ['2', '3', '4', '9'],
    answer: 1,
    explanation: [
      '기울기 = Δy / Δx = (y₂−y₁) / (x₂−x₁)',
      '= (11 − 2) / (4 − 1)',
      '= 9 / 3 = 3'
    ],
    concept: '변화율·기울기', misconceptionCode: 'M06', learnStepId: 'LS04',
  },
  {
    id: 'T07', number: 7, category: '함수식 세우기', difficulty: 'medium',
    question: '두 점 (0, −3)과 (2, 5)를 지나는 일차함수의 식은?',
    choices: ['y = 4x − 3', 'y = 4x + 3', 'y = 3x − 4', 'y = −4x − 3'],
    answer: 0,
    explanation: [
      '(0, −3)을 지나므로 y절편 b = −3',
      '기울기 a = (5−(−3))/(2−0) = 8/2 = 4',
      'y = 4x − 3'
    ],
    concept: '일차함수 식', learnStepId: 'LS07',
  },
  {
    id: 'T08', number: 8, category: '그래프 방향', difficulty: 'medium',
    question: '다음 중 x가 증가할 때 y가 감소하는 함수는?',
    choices: ['y = 3x + 1', 'y = 0.5x − 4', 'y = −2x + 7', 'y = x'],
    answer: 2,
    explanation: [
      'x가 증가할 때 y가 감소 ↔ 기울기 a < 0',
      '① a = 3 > 0 → 증가 ✗',
      '② a = 0.5 > 0 → 증가 ✗',
      '③ a = −2 < 0 → 감소 ✓',
      '④ a = 1 > 0 → 증가 ✗',
      '→ ③ y = −2x + 7 이 유일하게 감소하는 함수'
    ],
    concept: '기울기·방향성', misconceptionCode: 'M05', learnStepId: 'LS05',
    misconception: '기울기가 음수(a < 0)이면 x가 증가할 때 y가 감소(우하향)합니다!'
  },
  {
    id: 'T09', number: 9, category: '그래프 해석', difficulty: 'medium',
    question: '직선이 x절편이 4이고 y절편이 −2일 때, 이 직선의 기울기는?',
    choices: ['1/2', '−1/2', '2', '−2'],
    answer: 0,
    explanation: [
      '두 점: (4, 0)과 (0, −2)를 지남',
      '기울기 = (−2 − 0) / (0 − 4) = −2 / −4 = 1/2',
      '기울기 = 1/2'
    ],
    concept: '그래프 읽기·기울기', misconceptionCode: 'M07', learnStepId: 'LS10',
  },
  {
    id: 'T10', number: 10, category: '실생활 문제', difficulty: 'medium',
    question: '처음 온도가 80°C인 물이 1분마다 5°C씩 식는다. x분 후의 온도 y°C를 나타내는 함수식은?',
    choices: ['y = 5x + 80', 'y = −5x + 80', 'y = 5x − 80', 'y = 80x − 5'],
    answer: 1,
    explanation: [
      '변수 설정: x = 시간(분), y = 온도(°C)',
      '기울기: 1분마다 5°C씩 감소 → a = −5',
      '처음 온도(y절편): 80°C → b = 80',
      'y = −5x + 80'
    ],
    concept: '함수 모델링', misconceptionCode: 'M08', learnStepId: 'LS11',
    misconception: '"식는다" = 감소 = 음수 기울기! 기울기를 −5로 써야 합니다.'
  },
  {
    id: 'T11', number: 11, category: '교점', difficulty: 'hard',
    question: 'y = x + 3 과 y = −2x + 9 의 교점의 x좌표는?',
    choices: ['1', '2', '3', '4'],
    answer: 1,
    explanation: [
      '교점: 두 함수를 연립',
      'x + 3 = −2x + 9',
      'x + 2x = 9 − 3 → 3x = 6',
      'x = 2'
    ],
    concept: '연립방정식·교점', learnStepId: 'LS10',
  },
  {
    id: 'T12', number: 12, category: '함수 판단', difficulty: 'hard',
    question: '다음 중 일차함수인 것은?',
    choices: ['y = x²', 'y = 3/x', 'y = −2x + 5', 'y = 7'],
    answer: 2,
    explanation: [
      '일차함수: y = ax + b (a≠0) 형태 — x의 차수가 정확히 1이어야 함',
      '① y = x²: x의 차수가 2 → 이차함수 ✗',
      '② y = 3/x = 3x⁻¹: x가 분모 → 반비례 함수 ✗',
      '③ y = −2x + 5: a=−2≠0, x의 차수=1 → 일차함수 ✓',
      '④ y = 7 = 0x + 7: a=0 → 상수함수 ✗',
      '→ ③만 y = ax + b (a≠0) 조건을 만족!'
    ],
    concept: '일차함수 정의', misconceptionCode: 'M04', learnStepId: 'LS07',
    misconception: 'y = ax + b에서 a≠0 이어야 일차함수! a=0이면 상수함수, x²이면 이차함수입니다.'
  },
  {
    id: 'T13', number: 13, category: '평행 조건', difficulty: 'hard',
    question: 'y = 2x + k 가 직선 y = 2x − 5 와 평행할 때 조건으로 옳은 것은?',
    choices: ['k = −5', 'k ≠ −5', 'k = 5', 'k ≠ 5'],
    answer: 1,
    explanation: [
      '평행 조건: 기울기가 같고 y절편이 달라야 함',
      '두 직선의 기울기: 모두 2로 같음 (평행 가능성 있음)',
      '평행이 되려면 y절편이 달라야 함: k ≠ −5',
      '(k = −5이면 같은 직선이 됨)'
    ],
    concept: '평행 조건', learnStepId: 'LS10',
  },
  {
    id: 'T14', number: 14, category: '절편 활용', difficulty: 'hard',
    question: 'y = ax + b 에서 a < 0, b > 0 일 때, 그래프가 지나지 않는 사분면은?',
    choices: ['제1사분면', '제2사분면', '제3사분면', '제4사분면'],
    answer: 2,
    explanation: [
      'a < 0 → 기울기 음수 → 오른쪽으로 갈수록 감소',
      'b > 0 → y절편 양수 → y축의 위쪽을 통과',
      '음수 기울기 + 양수 y절편인 직선은 1, 2, 4사분면을 지남',
      '→ 제3사분면을 지나지 않음!'
    ],
    concept: '그래프·사분면', misconceptionCode: 'M07', learnStepId: 'LS10',
    misconception: '기울기 부호와 y절편 부호의 조합으로 통과 사분면을 결정합니다!'
  },
  {
    id: 'T15', number: 15, category: '실생활 계산', difficulty: 'hard',
    question: '택시 요금이 기본요금 3,000원에 1km마다 500원 추가될 때, 요금이 8,000원이 되는 거리는?',
    choices: ['8 km', '10 km', '12 km', '16 km'],
    answer: 1,
    explanation: [
      '함수 모델: y = 500x + 3000 (x: 거리, y: 요금)',
      '8000 = 500x + 3000',
      '500x = 5000 → x = 10',
      '→ 10 km'
    ],
    concept: '함수 모델링·실생활', learnStepId: 'LS11',
  },
  {
    id: 'T16', number: 16, category: '조건 추론', difficulty: 'hard',
    question: 'x의 값이 4만큼 증가할 때 y의 값이 12만큼 증가하는 일차함수의 기울기는?',
    choices: ['1/3', '3', '8', '48'],
    answer: 1,
    explanation: [
      '기울기 = Δy / Δx = y의 변화량 / x의 변화량',
      'Δy = 12, Δx = 4',
      'a = 12 / 4 = 3'
    ],
    concept: '변화율·기울기', misconceptionCode: 'M06', learnStepId: 'LS04',
  },
  {
    id: 'T17', number: 17, category: '식에서 값 계산', difficulty: 'medium',
    question: 'y = −2x + 5 에서 x = −3 일 때 y의 값은?',
    choices: ['−1', '1', '11', '−11'],
    answer: 2,
    explanation: [
      'y = −2x + 5 에 x = −3 대입',
      'y = −2 × (−3) + 5',
      '= 6 + 5 = 11'
    ],
    concept: '함수값 계산', learnStepId: 'LS07',
  },
  {
    id: 'T18', number: 18, category: '역함수 개념', difficulty: 'medium',
    question: 'y = 3x + 6 에서 y = 0 일 때 x의 값은?',
    choices: ['−2', '2', '6', '−6'],
    answer: 0,
    explanation: [
      'y = 0 을 y = 3x + 6 에 대입',
      '0 = 3x + 6',
      '3x = −6 → x = −2',
      '(이것이 x절편입니다!)'
    ],
    concept: 'x절편·역계산', learnStepId: 'LS09',
  },
  {
    id: 'T19', number: 19, category: '넓이 계산', difficulty: 'hard',
    question: 'y = x + 4 의 그래프와 두 축으로 만들어지는 삼각형의 넓이는?',
    choices: ['4', '8', '16', '2'],
    answer: 1,
    explanation: [
      'x절편: y=0 → 0 = x+4 → x = −4, 점 (−4, 0)',
      'y절편: x=0 → y = 4, 점 (0, 4)',
      '삼각형 꼭짓점: O(0,0), (−4, 0), (0, 4)',
      '밑변 = |−4| = 4, 높이 = 4',
      '넓이 = (1/2) × 4 × 4 = 8'
    ],
    concept: '넓이 계산·응용', learnStepId: 'LS10',
  },
  {
    id: 'T20', number: 20, category: '종합', difficulty: 'hard',
    question: '일차함수 y = ax + b 에 대해\na = (두 점 (1,3)과 (3,7)로 구한 기울기),\nb = (기울기 a, 점 (1,3)으로 구한 y절편)\n일 때, a × b 의 값은?',
    choices: ['2', '4', '1', '−2'],
    answer: 0,
    explanation: [
      '① 기울기 a 계산: a = (7−3)/(3−1) = 4/2 = 2',
      '② y절편 b 계산: y = 2x + b 에 (1,3) 대입',
      '   3 = 2(1) + b → b = 1',
      '③ a × b = 2 × 1 = 2'
    ],
    concept: '종합·기울기·y절편', learnStepId: 'LS12',
  },
]

// ────────────────────────────────────────────────────────────────────────────
// 등급 설정
// ────────────────────────────────────────────────────────────────────────────
function getGrade(score: number): { grade: string; label: string; color: string; emoji: string; message: string } {
  if (score >= 95) return { grade: 'A+', label: '최우수', color: 'text-purple-700', emoji: '🏆', message: '완벽합니다! 일차함수 마스터!' }
  if (score >= 90) return { grade: 'A',  label: '우수',   color: 'text-blue-700',   emoji: '🥇', message: '매우 훌륭합니다! 거의 완벽해요!' }
  if (score >= 80) return { grade: 'B',  label: '양호',   color: 'text-emerald-700',emoji: '🥈', message: '잘 했어요! 약간의 복습이 필요합니다.' }
  if (score >= 70) return { grade: 'C',  label: '보통',   color: 'text-yellow-700', emoji: '🥉', message: '기본은 됩니다. 오답 개념을 다시 확인해보세요.' }
  if (score >= 60) return { grade: 'D',  label: '미흡',   color: 'text-orange-700', emoji: '📚', message: '조금 더 노력이 필요합니다. 개념 학습부터 다시!' }
  return { grade: 'F', label: '부족', color: 'text-red-700', emoji: '💪', message: '포기하지 마세요! 처음부터 차근차근 다시 학습하세요.' }
}

const DIFF_COLOR = {
  easy:   'bg-emerald-100 text-emerald-700',
  medium: 'bg-blue-100 text-blue-700',
  hard:   'bg-red-100 text-red-700',
}
const DIFF_LABEL = { easy: '쉬움', medium: '보통', hard: '어려움' }

// 학습 단계 ID → 제목 매핑 (LearnPage STEPS와 완전 일치)
const STEP_LABELS: Record<string, string> = {
  LS01: '변수 이해',
  LS02: '순서쌍과 좌표평면',
  LS03: '표에서 함수 관계 찾기',
  LS04: '변화율 (율)',
  LS05: '기울기(slope) 이해',
  LS06: 'y절편 이해',
  LS07: '1차 함수식 y = ax + b',
  LS08: '정비례 vs 1차 함수',
  LS09: '그래프 그리기',
  LS10: '그래프에서 식 구하기',
  LS11: '실생활 문장제 → 함수식',
  LS12: '오답 분석과 개념 정리',
}

// ────────────────────────────────────────────────────────────────────────────
// 시험 중 — 문항 표시 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
function QuestionView({ q, selected, onSelect }: {
  q: TestQuestion
  selected: number | null
  onSelect: (idx: number) => void
}) {
  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 text-white rounded-lg flex items-center justify-center text-sm font-black">
          {q.number}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${DIFF_COLOR[q.difficulty]}`}>
          {DIFF_LABEL[q.difficulty]}
        </span>
        <span className="text-xs text-slate-400 font-mono">{q.category}</span>
      </div>
      <p className="text-sm font-semibold text-slate-800 leading-relaxed mb-4 whitespace-pre-line">
        {q.question}
      </p>
      <div className="space-y-2">
        {q.choices.map((choice, i) => (
          <button
            key={i}
            onClick={() => onSelect(i)}
            className={`w-full text-left px-4 py-2.5 rounded-xl border-2 text-sm transition-all ${
              selected === i
                ? 'border-blue-500 bg-blue-50 text-blue-800 font-semibold'
                : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50 text-slate-700'
            }`}
          >
            <span className="font-bold mr-2 text-slate-400">{['①','②','③','④'][i]}</span>
            {choice}
          </button>
        ))}
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// 결과 화면 — 오답 풀이 카드 (개념 탭 연결 포함)
// ────────────────────────────────────────────────────────────────────────────
function WrongAnswerCard({ q, userAnswer, isOpen, onToggle, onGoLearn }: {
  q: TestQuestion
  userAnswer: number | null
  isOpen: boolean
  onToggle: () => void
  onGoLearn: (stepId: string) => void
}) {
  const [videoModal, setVideoModal] = useState(false)
  const getApprovedByTag   = useVideoStore(s => s.getApprovedByTag)
  const getRecruitingByTag = useVideoStore(s => s.getRecruitingByTag)
  // learnStepId(LS05 등) 우선, 없으면 concept 태그로 검색
  const conceptKey     = q.learnStepId ?? q.concept
  const approvedVideos = getApprovedByTag(conceptKey)
  const hasApproved    = approvedVideos.length > 0
  const hasRecruiting  = !hasApproved && getRecruitingByTag(conceptKey).length > 0
  const isCorrect = userAnswer === q.answer
  const isSkipped = userAnswer === null

  return (
    <div className={`rounded-2xl border-2 overflow-hidden ${
      isCorrect ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'
    }`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-center gap-3 hover:bg-white/50 transition-colors"
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-black text-white flex-shrink-0 ${
          isCorrect ? 'bg-emerald-500' : 'bg-red-500'
        }`}>
          {q.number}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            {isCorrect
              ? <span className="text-xs font-bold text-emerald-600 flex items-center gap-1"><CheckCircle size={11} /> 정답</span>
              : isSkipped
                ? <span className="text-xs font-bold text-slate-400">미응답</span>
                : <span className="text-xs font-bold text-red-600 flex items-center gap-1"><XCircle size={11} /> 오답</span>
            }
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${DIFF_COLOR[q.difficulty]}`}>
              {DIFF_LABEL[q.difficulty]}
            </span>
            <span className="text-xs text-slate-400">{q.category}</span>
          </div>
          <p className="text-xs text-slate-600 truncate">{q.question.split('\n')[0]}</p>
        </div>
        {!isCorrect && (
          isOpen ? <ChevronUp size={16} className="text-slate-400 flex-shrink-0" />
                 : <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
        )}
      </button>

      {/* 오답일 때 풀이 표시 */}
      {!isCorrect && isOpen && (
        <div className="border-t border-red-100 p-4 bg-white">
          {/* 선택 vs 정답 */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs font-bold text-red-500 mb-1">내 답 {isSkipped ? '(미응답)' : ''}</p>
              <p className="text-sm font-semibold text-red-700">
                {isSkipped ? '—' : `${['①','②','③','④'][userAnswer!]} ${q.choices[userAnswer!]}`}
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-xs font-bold text-emerald-500 mb-1">✅ 정답</p>
              <p className="text-sm font-semibold text-emerald-700">
                {['①','②','③','④'][q.answer]} {q.choices[q.answer]}
              </p>
            </div>
          </div>

          {/* 단계별 풀이 */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3">
            <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
              <PenTool size={11} /> 단계별 풀이
            </p>
            <div className="space-y-2">
              {q.explanation.map((line, i) => (
                <div key={i} className="flex gap-2">
                  <span className="w-5 h-5 bg-blue-500 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 font-bold">
                    {i + 1}
                  </span>
                  <p className="text-xs text-slate-700 leading-relaxed whitespace-pre-line">{line}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 오개념 경고 */}
          {q.misconception && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3 flex items-start gap-2">
              <AlertTriangle size={14} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">{q.misconception}</p>
            </div>
          )}

          {/* 연결 개념 + 액션 버튼들 ★★★ */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-400">연결 개념:</span>
              <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-mono">{q.concept}</span>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* 🎬 영상으로 복습 — 파편 학습 핵심 버튼 (3단계 UI) */}
              {hasApproved ? (
                <button
                  onClick={() => setVideoModal(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl font-bold transition-all shadow-sm bg-gradient-to-r from-red-500 to-rose-500 text-white hover:opacity-90"
                  title={`승인된 영상 ${approvedVideos.length}개`}
                >
                  <Youtube size={11} />
                  🎬 영상으로 복습 ({approvedVideos.length})
                </button>
              ) : hasRecruiting ? (
                <button
                  onClick={() => setVideoModal(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl font-bold transition-all shadow-sm bg-violet-100 text-violet-700 border border-violet-300 hover:bg-violet-200"
                  title="영상 크리에이터 모집 중"
                >
                  <Youtube size={11} />
                  🎬 영상 모집 중
                </button>
              ) : (
                <button
                  onClick={() => setVideoModal(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl font-bold transition-all shadow-sm bg-slate-100 text-slate-400 hover:bg-slate-200"
                  title="아직 영상 없음"
                >
                  <Youtube size={11} />
                  영상 복습
                </button>
              )}
              {/* 📖 텍스트 개념 학습 */}
              {q.learnStepId && (
                <button
                  onClick={() => onGoLearn(q.learnStepId!)}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 bg-blue-100 text-blue-700 rounded-xl font-bold hover:bg-blue-200 transition-colors"
                >
                  <BookOpen size={11} />
                  {STEP_LABELS[q.learnStepId] ?? q.learnStepId} 개념 보기
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {/* 파편 학습 영상 모달 */}
      {videoModal && (
        <ConceptVideoModal
          conceptTag={q.learnStepId ?? q.concept}
          conceptLabel={q.concept}
          onClose={() => setVideoModal(false)}
          onCompleted={() => setVideoModal(false)}
        />
      )}
    </div>
  )
}

// ────────────────────────────────────────────────────────────────────────────
// 메인 TEST 페이지
// ────────────────────────────────────────────────────────────────────────────
type Phase = 'intro' | 'test' | 'result'

export default function TestPage({ onGoLearn }: { onGoLearn?: (stepId: string) => void }) {
  const [phase, setPhase] = useState<Phase>('intro')
  const [answers, setAnswers] = useState<(number | null)[]>(Array(20).fill(null))
  const [currentQ, setCurrentQ] = useState(0)
  const [seconds, setSeconds] = useState(0)
  const [openReviewIds, setOpenReviewIds] = useState<Set<string>>(new Set())
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // progressStore 연동
  const { recordTestResult, recordMisconceptions, init, getUnit } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
    init: s.init,
    getUnit: s.getUnit,
  }))

  useEffect(() => { init('student_001') }, [])

  // 이전 TEST 기록 불러오기
  const unitData = getUnit(UNIT_ID)
  const bestScore = unitData.bestScore
  const testCount = unitData.testRecords.length
  const isPassed  = unitData.passed

  // 타이머
  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
    const ss = s % 60
    return `${m.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`
  }

  const handleAnswer = (idx: number) => {
    const next = [...answers]
    next[currentQ] = idx
    setAnswers(next)
  }

  const submitTest = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)

    // ── progressStore에 결과 저장 ──────────────────────────────
    const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correctCount / 20) * 100)
    const gi = getGrade(sc)

    // 틀린 문제의 misconception 코드 수집
    const wrongMCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    const wrongConcepts = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer)
      .map(q => q.concept)

    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount,
      totalCount: 20,
      durationSec: seconds,
      answers,
      wrongConceptIds: wrongConcepts,
      grade: gi.grade,
      passed: sc >= PASS_SCORE,
    })
    if (wrongMCodes.length > 0) {
      recordMisconceptions(UNIT_ID, wrongMCodes)
    }
    // ─────────────────────────────────────────────────────────

    setPhase('result')
  }

  const resetTest = () => {
    setAnswers(Array(20).fill(null))
    setCurrentQ(0)
    setSeconds(0)
    setOpenReviewIds(new Set())
    setPhase('intro')
  }

  // 결과 계산
  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / 20) * 100)
  const gradeInfo = getGrade(score)
  const answeredCount = answers.filter(a => a !== null).length
  const skippedCount = 20 - answeredCount

  const toggleReview = (id: string) => {
    setOpenReviewIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // 카테고리별 분석
  const categoryStats = TEST_QUESTIONS.reduce((acc, q, i) => {
    if (!acc[q.category]) acc[q.category] = { total: 0, correct: 0 }
    acc[q.category].total++
    if (answers[i] === q.answer) acc[q.category].correct++
    return acc
  }, {} as Record<string, { total: number; correct: number }>)

  // ────────────────────────────────────────────────────────────────────────
  // 인트로 화면
  // ────────────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Trophy size={40} className="text-white" />
          </div>
          <h1 className="text-3xl font-black text-slate-800 mb-2">실전 TEST</h1>
          <p className="text-slate-500">일차함수와 그래프 — 최종 실력 점검</p>

          {/* 이전 기록 배지 */}
          {testCount > 0 && (
            <div className="mt-3 flex justify-center gap-3 flex-wrap">
              <span className={`text-sm px-3 py-1.5 rounded-full font-bold border-2 ${
                isPassed ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'
              }`}>
                {isPassed ? '✅ 합격 완료' : '⏳ 미합격'} | 최고점 {bestScore}점
              </span>
              <span className="text-sm px-3 py-1.5 rounded-full font-bold border-2 bg-blue-50 border-blue-200 text-blue-700">
                {testCount}회 응시
              </span>
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          {[
            { icon: <FileText size={20} />, label: '총 문항', value: '20문제', color: 'bg-blue-50 border-blue-200 text-blue-700' },
            { icon: <Clock size={20} />, label: '권장 시간', value: '20분', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
            { icon: <Zap size={20} />, label: '배점', value: '5점/문제', color: 'bg-orange-50 border-orange-200 text-orange-700' },
            { icon: <Award size={20} />, label: '합격 기준', value: `${PASS_SCORE}점 이상`, color: 'bg-purple-50 border-purple-200 text-purple-700' },
          ].map((item, i) => (
            <div key={i} className={`rounded-2xl border-2 p-4 flex items-center gap-3 ${item.color}`}>
              <div className="opacity-70">{item.icon}</div>
              <div>
                <p className="text-xs font-semibold opacity-60">{item.label}</p>
                <p className="text-lg font-black">{item.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 mb-6">
          <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
            <BarChart2 size={16} className="text-blue-500" /> 출제 영역
          </h3>
          <div className="space-y-2">
            {[
              { label: '쉬움 (easy)', count: 5, color: 'bg-emerald-400' },
              { label: '보통 (medium)', count: 7, color: 'bg-blue-400' },
              { label: '어려움 (hard)', count: 8, color: 'bg-red-400' },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-3">
                <span className="text-xs text-slate-500 w-32">{item.label}</span>
                <div className="flex-1 bg-slate-200 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full ${item.color}`}
                    style={{ width: `${(item.count / 20) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-slate-600">{item.count}문제</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
          <h3 className="font-bold text-blue-800 mb-2 flex items-center gap-2">
            <Brain size={15} /> 등급 기준
          </h3>
          <div className="grid grid-cols-3 gap-2 text-center">
            {[
              { grade:'A+', range:'95~100', color:'text-purple-700' },
              { grade:'A',  range:'90~94',  color:'text-blue-700' },
              { grade:'B',  range:'80~89',  color:'text-emerald-700' },
              { grade:'C',  range:'70~79',  color:'text-yellow-700' },
              { grade:'D',  range:'60~69',  color:'text-orange-700' },
              { grade:'F',  range:'0~59',   color:'text-red-700' },
            ].map(g => (
              <div key={g.grade} className={`bg-white rounded-lg py-1.5 border ${g.grade === 'C' || g.grade === 'B' || g.grade === 'A' || g.grade === 'A+' ? 'border-emerald-200' : 'border-slate-100'}`}>
                <p className={`text-lg font-black ${g.color}`}>{g.grade}</p>
                <p className="text-xs text-slate-400">{g.range}점</p>
                {g.range.startsWith('70') && (
                  <p className="text-xs text-emerald-600 font-bold">합격</p>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-blue-600 mt-2 text-center font-semibold">{PASS_SCORE}점 이상 = 단원 합격 🎯</p>
        </div>

        <button
          onClick={() => setPhase('test')}
          className="w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl font-black text-lg hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg flex items-center justify-center gap-2"
        >
          <Zap size={20} /> {testCount > 0 ? `다시 시험 (${testCount + 1}회)` : '시험 시작'}
        </button>
        <p className="text-center text-xs text-slate-400 mt-3">시험을 시작하면 타이머가 작동합니다</p>
      </div>
    )
  }

  // ────────────────────────────────────────────────────────────────────────
  // 시험 중 화면
  // ────────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[currentQ]
    const progressPct = ((currentQ + 1) / 20) * 100

    return (
      <div className="p-4 max-w-2xl mx-auto">
        {/* 헤더 */}
        <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-4 flex items-center gap-3 shadow-sm">
          <div className="flex-1">
            <div className="flex justify-between text-xs text-slate-500 mb-1">
              <span>{currentQ + 1} / 20 문제</span>
              <span>{answeredCount} 문제 완료</span>
            </div>
            <div className="bg-slate-100 rounded-full h-2.5">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-2.5 rounded-full transition-all duration-300"
                style={{ width: `${progressPct}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-slate-600 bg-slate-100 rounded-xl px-3 py-1.5">
            <Clock size={14} />
            <span className="font-mono text-sm font-bold">{formatTime(seconds)}</span>
          </div>
        </div>

        {/* 문항 번호 빠른 이동 */}
        <div className="flex gap-1 flex-wrap mb-4">
          {TEST_QUESTIONS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentQ(i)}
              className={`w-7 h-7 rounded-lg text-xs font-bold transition-all ${
                i === currentQ
                  ? 'bg-blue-500 text-white shadow-md'
                  : answers[i] !== null
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>

        {/* 현재 문항 */}
        <QuestionView q={q} selected={answers[currentQ]} onSelect={handleAnswer} />

        {/* 이전/다음 + 제출 */}
        <div className="flex items-center gap-3 mt-4">
          <button
            onClick={() => setCurrentQ(Math.max(0, currentQ - 1))}
            disabled={currentQ === 0}
            className="px-4 py-2.5 rounded-xl border-2 border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-30 transition-all"
          >
            ← 이전
          </button>
          <div className="flex-1" />
          {currentQ < 19 ? (
            <button
              onClick={() => setCurrentQ(Math.min(19, currentQ + 1))}
              className="px-5 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition-all flex items-center gap-1.5"
            >
              다음 <ChevronRight size={14} />
            </button>
          ) : (
            <button
              onClick={submitTest}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-black hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg flex items-center gap-1.5"
            >
              <Trophy size={14} /> 제출하기
            </button>
          )}
        </div>

        {/* 제출 버튼 (언제든지) */}
        {currentQ < 19 && (
          <div className="mt-4 text-center">
            <button
              onClick={submitTest}
              className="text-xs text-slate-400 hover:text-slate-600 underline transition-colors"
            >
              {skippedCount > 0 ? `${skippedCount}문제 미응답 포함하여` : ''} 지금 제출하기
            </button>
          </div>
        )}
      </div>
    )
  }

  // ────────────────────────────────────────────────────────────────────────
  // 결과 화면
  // ────────────────────────────────────────────────────────────────────────
  const wrongQuestions = TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer)
  const correctQuestions = TEST_QUESTIONS.filter((q, i) => answers[i] === q.answer)
  const passed = score >= PASS_SCORE

  return (
    <div className="p-4 max-w-3xl mx-auto">
      {/* 최상단 성적 카드 */}
      <div className="bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-3xl p-6 text-white text-center mb-6 shadow-xl">
        <div className="text-5xl mb-2">{gradeInfo.emoji}</div>
        <div className={`text-7xl font-black mb-1`}>{gradeInfo.grade}</div>
        <p className="text-blue-100 text-sm mb-3">{gradeInfo.label}</p>
        <div className="text-5xl font-black mb-1">{score}점</div>
        <p className="text-blue-200 text-sm mb-2">{correctCount} / 20 문제 정답</p>
        {/* 합격/불합격 배지 */}
        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-black text-sm mb-3 ${
          passed ? 'bg-emerald-400/30 border-2 border-emerald-300' : 'bg-red-400/30 border-2 border-red-300'
        }`}>
          {passed ? '🎉 합격! 단원 완료' : `🎯 목표: ${PASS_SCORE}점 (${PASS_SCORE - score}점 더 필요)`}
        </div>
        <div className="bg-white/20 rounded-2xl p-3">
          <p className="text-white text-sm font-semibold">{gradeInfo.message}</p>
        </div>
      </div>

      {/* 상세 통계 */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3 text-center">
          <p className="text-xs text-emerald-600 font-semibold mb-1">✅ 정답</p>
          <p className="text-3xl font-black text-emerald-700">{correctCount}</p>
          <p className="text-xs text-emerald-500">문제</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-3 text-center">
          <p className="text-xs text-red-600 font-semibold mb-1">❌ 오답</p>
          <p className="text-3xl font-black text-red-700">{wrongQuestions.length}</p>
          <p className="text-xs text-red-500">문제</p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3 text-center">
          <p className="text-xs text-slate-600 font-semibold mb-1">⏱ 소요</p>
          <p className="text-2xl font-black text-slate-700">{formatTime(seconds)}</p>
          <p className="text-xs text-slate-500">분:초</p>
        </div>
      </div>

      {/* 카테고리별 분석 */}
      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-6">
        <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
          <BarChart2 size={16} className="text-blue-500" /> 영역별 분석
        </h3>
        <div className="space-y-2">
          {Object.entries(categoryStats).map(([cat, stat]) => {
            const pct = Math.round((stat.correct / stat.total) * 100)
            return (
              <div key={cat} className="flex items-center gap-3">
                <span className="text-xs text-slate-600 w-28 font-medium truncate">{cat}</span>
                <div className="flex-1 bg-slate-100 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      pct >= 80 ? 'bg-emerald-400' : pct >= 60 ? 'bg-yellow-400' : 'bg-red-400'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className="text-xs font-bold text-slate-600 w-12 text-right">
                  {stat.correct}/{stat.total}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      {/* 오답 풀이 섹션 */}
      {wrongQuestions.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-red-500 rounded-xl flex items-center justify-center">
              <XCircle size={16} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">오답 풀이</h3>
              <p className="text-xs text-slate-500">{wrongQuestions.length}문제 틀림 — 클릭하면 단계별 풀이 + 학습 이동</p>
            </div>
            <button
              onClick={() => setOpenReviewIds(new Set(wrongQuestions.map(q => q.id)))}
              className="ml-auto text-xs px-3 py-1.5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 transition-colors"
            >
              전체 펼치기
            </button>
          </div>
          <div className="space-y-3">
            {TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map((q) => (
              <WrongAnswerCard
                key={q.id}
                q={q}
                userAnswer={answers[q.number - 1]}
                isOpen={openReviewIds.has(q.id)}
                onToggle={() => toggleReview(q.id)}
                onGoLearn={(stepId) => {
                  if (onGoLearn) onGoLearn(stepId)
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* 정답 섹션 (간략) */}
      {correctQuestions.length > 0 && (
        <div className="mb-6">
          <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-emerald-500" /> 맞힌 문제 ({correctCount}문제)
          </h3>
          <div className="flex flex-wrap gap-2">
            {correctQuestions.map(q => (
              <span key={q.id} className="text-xs px-2.5 py-1 bg-emerald-100 text-emerald-700 rounded-full font-semibold">
                ✓ {q.number}번
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 학습 추천 */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-4 mb-6">
        <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
          <Lightbulb size={16} /> 학습 추천
        </h3>
        <p className="text-sm text-amber-700 mb-3">
          {score >= 90
            ? '훌륭합니다! 이차함수 등 다음 단원으로 도전해보세요.'
            : score >= PASS_SCORE
              ? `합격! 틀린 ${wrongQuestions.length}문제의 개념을 다시 복습하고, 문제풀이 탭에서 유사 유형을 연습하세요.`
              : `아직 합격 점수(${PASS_SCORE}점)에 미달입니다. 오답 개념을 집중적으로 복습하세요.`
          }
        </p>
        {wrongQuestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {[...new Set(wrongQuestions.map(q => q.concept))].map(concept => (
              <span key={concept} className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                📌 {concept}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 다시 시험 */}
      <button
        onClick={resetTest}
        className="w-full py-3.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-2xl font-black text-base hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg flex items-center justify-center gap-2"
      >
        <RotateCcw size={18} /> 다시 시험 보기
      </button>
    </div>
  )
}
